/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';
import { Format } from '../model/format.model';
import { Locale } from '../model/locale.model';
import { FormatterProvider } from '../provider/formatter.provider';
import { LocaleChangedEvent, LocaleProvider } from '../provider/locale.provider';
import { TextProvider } from '../provider/text.provider';
import { ResourcesProvider } from '../provider/resources.provider';

export class LocaleService {
  private localeKeySubject: BehaviorSubject<string>;
  private resolveLocalesPromises: Set<Promise<any>>;
  private resolveLocalesCallbacks: Array<(error: ApplicationError) => void>;

  constructor() {
    this.localeKeySubject = new BehaviorSubject<string>(null);
    this.resolveLocalesPromises = new Set<Promise<void>>();
    this.resolveLocalesCallbacks = null;
    LocaleProvider.localeChanged.subscribe((event) => this.onPackageLocaleChanged(event));
  }

  get localeKey(): string {
    return this.localeKeySubject.getValue();
  }

  get localeKeyChanged(): Observable<string> {
    return this.localeKeySubject.asObservable();
  }

  async resolveLocaleKey(): Promise<string> {
    return this.resolveLocales(() => this.localeKey);
  }

  async setLocale(localeKey: string): Promise<void> {
    if (localeKey === this.localeKey) {
      return;
    }
    const setPackageLocalesPromise = this.internalSetPackageLocales(localeKey);
    this.resolveLocalesPromises.add(setPackageLocalesPromise);
    return setPackageLocalesPromise
      .catch((error: ApplicationError) => error)
      .then((error: ApplicationError) => {
        this.resolveLocalesPromises.delete(setPackageLocalesPromise);
        this.invokeResolveLocaleCallbacks(error);
        if (error) {
          throw error;
        }
      });
  }

  async resolveLocales<T>(result?: T | (() => T)): Promise<T> {
    if (this.resolveLocalesPromises.size === 0) {
      return typeof result === 'function' ? (result as (() => T))() : result;
    }
    return new Promise<T>((resolve, reject) => {
      const internalCallback = async (error: ApplicationError) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(typeof result === 'function' ? (result as (() => T))() : result);
      };
      if (this.resolveLocalesCallbacks) {
        this.resolveLocalesCallbacks.push(internalCallback);
      } else {
        this.resolveLocalesCallbacks = [internalCallback];
      }
    });
  }

  private async internalSetPackageLocales(localeKey: string): Promise<void> {
    const locales = await LocaleProvider.resolvePackageLocales(localeKey);
    if (!locales) {
      throw new ApplicationError({
        key: CoreErrorKey.LOCALE_NOT_FOUND,
        arguments: { localeKey },
        message: `Locale '${localeKey || ''}' not found`
      });
    }
    Object.keys(locales).forEach((packageKey: string) => {
      const locale: Locale = locales[packageKey];
      this.internalSetPackageLocale(packageKey, locale);
    });
    moment.locale(localeKey);
    this.localeKeySubject.next(localeKey);
  }

  private internalSetPackageLocale(packageKey: string, locale: Locale) {
    if (locale.texts) {
      TextProvider.registerTextPatterns(packageKey, locale.texts);
    }
    if (locale.resources) {
      ResourcesProvider.registerResources(packageKey, locale.resources);
    }
    if (locale.formats) {
      Object.keys(locale.formats).forEach((formatKey: string) => {
        const format: Format = locale.formats[formatKey];
        const formatterFactory = FormatterProvider.resolveFormatterFactory(format.formatterKey);
        if (!formatterFactory) {
          return;
        }
        const formatter = formatterFactory(format.formatterOptions);
        FormatterProvider.registerFormatter(formatKey, formatter);
        FormatterProvider.registerFormatter(`${packageKey}/format/${formatKey}`, formatter);
      });
    }
  }

  private async onPackageLocaleChanged(event: LocaleChangedEvent) {
    if (this.localeKey !== event.localeKey) {
      // if locale key does not match, package locale need not to be resolved
      return;
    }
    const resolvePackageLocalePromise = LocaleProvider.resolvePackageLocale(event.localeKey, event.packageKey);
    this.resolveLocalesPromises.add(resolvePackageLocalePromise);
    return resolvePackageLocalePromise
      .then((locale: Locale) => {
        if (this.localeKey !== event.localeKey) {
          // if locale key was changed, while package locale was resolved, do nothing
          return;
        }
        this.internalSetPackageLocale(event.packageKey, locale);
      })
      .catch((error: ApplicationError) => error)
      .then((error: ApplicationError) => {
        this.resolveLocalesPromises.delete(resolvePackageLocalePromise);
        this.invokeResolveLocaleCallbacks(error);
        if (error) {
          throw error;
        }
      });
  }

  private async invokeResolveLocaleCallbacks(error: ApplicationError) {
    if (!this.resolveLocalesCallbacks || this.resolveLocalesPromises.size > 0) {
      return;
    }
    const resolveLocaleCallbacks = this.resolveLocalesCallbacks;
    this.resolveLocalesCallbacks = null;
    resolveLocaleCallbacks.forEach((callback) => callback(error));
  }

}
